/**
 * A thin lens: one ideal plane through its optical centre (engine target §3).
 *
 * Its two radii are physics, through one number: the lensmaker's equation turns
 * R₁, R₂ and n into f, which is what makes biconvex, plano-convex and meniscus
 * one kind. Its drawn thickness is not physics at all.
 */
import { PartKind, numbered } from '../registry';
import { heights, localToWorld, sag } from '../shapes';
import { Fact, Part, ThinElement, Vec2, World } from '../types';
import { fromAngle, toWorld } from '../vec';

export interface LensData {
    /** Signed radii, cm, positive when the centre of curvature is on the far side
     *  from light arriving along the lens's facing. null is a flat face. */
    r1: number | null;
    r2: number | null;
    n: number;
    aperture: number;
    /** Used instead of the radii when `hold` is 'f'. */
    f: number;
    /** Whichever the student moved last is held; the other is worked out. */
    hold: 'radii' | 'f';
    [key: string]: unknown;
}

const inv = (r: number | null): number => (r === null || !Number.isFinite(r) ? 0 : 1 / r);

/** 1/f = (n/n_medium − 1)(1/R₁ − 1/R₂). Infinity for a lens with no power. */
export function lensmakerF(d: Pick<LensData, 'r1' | 'r2' | 'n'>, medium: number): number {
    const power = (d.n / medium - 1) * (inv(d.r1) - inv(d.r2));
    return Math.abs(power) < 1e-15 ? Infinity : 1 / power;
}

export const focalLength = (p: Part<LensData>, w: World): number =>
    p.data.hold === 'f' ? p.data.f : lensmakerF(p.data, w.medium);

/** What the shape is called. A face is convex when it bulges towards the light it meets. */
export function shapeName(d: Pick<LensData, 'r1' | 'r2'>, f: number): string {
    const face = (r: number | null, bulgesWhenPositive: boolean): 'plano' | 'convex' | 'concave' =>
        r === null || !Number.isFinite(r) ? 'plano' : (r > 0) === bulgesWhenPositive ? 'convex' : 'concave';
    const a = face(d.r1, true), b = face(d.r2, false);
    if (a === b) return a === 'plano' ? 'flat plate' : a === 'convex' ? 'biconvex' : 'biconcave';
    if (a === 'plano' || b === 'plano') return `plano-${a === 'plano' ? b : a}`;
    return f > 0 ? 'converging meniscus' : 'diverging meniscus';
}

const MIN_CENTRE = 0.3;
const MIN_EDGE = 0.3;

/** The drawn body, in the lens's own frame: light's first face on the −x side. */
function body(d: LensData): Vec2[] {
    const h = d.aperture / 2;
    // Thick enough at the centre and at the rim, whichever is tighter.
    const edgeGain = sag(d.r2, h) - sag(d.r1, h);
    const T = Math.max(MIN_CENTRE, MIN_EDGE - edgeGain);
    const ys = heights(h);
    const front = ys.map(y => ({ x: -T / 2 + sag(d.r1, y), y }));
    const back = [...ys].reverse().map(y => ({ x: T / 2 + sag(d.r2, y), y }));
    return [...front, ...back];
}

export const lens: PartKind<LensData> = {
    key: 'lens',
    label: 'Lens',
    tag: 'L',
    freedom: { onAxis: true, turns: true },

    create: (at: Vec2, id: string): Part<LensData> => ({
        id, kind: 'lens', at: { x: at.x, y: 0 }, angle: 0, mount: 'axis',
        data: { r1: 20, r2: -20, n: 1.5, aperture: 14, f: 20, hold: 'radii' },
    }),

    element(p, w): ThinElement {
        return {
            partId: p.id, centre: p.at, axis: fromAngle(p.angle),
            f: focalLength(p, w), folds: false, halfAperture: p.data.aperture / 2,
        };
    },

    surfaces(p, w) {
        const h = p.data.aperture / 2;
        return [{
            id: `${p.id}:plane`, partId: p.id,
            shape: { form: 'segment', a: toWorld(p.at, p.angle, { x: 0, y: -h }), b: toWorld(p.at, p.angle, { x: 0, y: h }) },
            rule: { law: 'ideal', f: focalLength(p, w), folds: false, centre: p.at, axis: fromAngle(p.angle) },
            name: numbered(p as Part, w, 'L'),
        }];
    },

    outline: (p) => localToWorld(p as Part, body(p.data)),

    controls: () => [
        {
            type: 'number', key: 'r1', label: 'R₁ (first face)', unit: 'cm', min: -200, max: 200, step: 0.5,
            nullLabel: 'flat', minMagnitude: 5, sets: { hold: 'radii' },
        },
        {
            type: 'number', key: 'r2', label: 'R₂ (second face)', unit: 'cm', min: -200, max: 200, step: 0.5,
            nullLabel: 'flat', minMagnitude: 5, sets: { hold: 'radii' },
        },
        { type: 'number', key: 'n', label: 'Refractive index n', min: 1, max: 2.5, step: 0.01, sets: { hold: 'radii' } },
        {
            type: 'number', key: 'f', label: 'Focal length f', unit: 'cm', min: -500, max: 500, step: 0.5,
            minMagnitude: 2, sets: { hold: 'f' },
        },
        { type: 'number', key: 'aperture', label: 'Aperture', unit: 'cm', min: 2, max: 30, step: 0.5 },
        {
            type: 'choice', key: 'hold', label: 'Set by',
            choices: [{ value: 'radii', label: 'radii and n' }, { value: 'f', label: 'focal length' }],
        },
    ],

    // Whichever is not held follows the one that is: f from the radii. (Held f
    // leaves the radii alone — a focal length does not decide a shape.)
    derive: (p, w) => {
        if (p.data.hold !== 'radii') return {};
        const f = lensmakerF(p.data, w.medium);
        return Number.isFinite(f) ? { f } : {};
    },

    keyFacts(p, w): Fact[] {
        const f = focalLength(p, w);
        const facts: Fact[] = [
            { label: 'Shape', value: shapeName(p.data, f) },
            { label: 'Focal length', value: Number.isFinite(f) ? `${f.toFixed(1)} cm` : 'no focus (no power)' },
            { label: 'Power', value: Number.isFinite(f) ? `${(100 / f).toFixed(2)} D` : '0 D' },
        ];
        if (p.data.hold === 'radii') {
            // A flat face contributes 1/∞ = 0; a curved one is written signed and bracketed.
            const inverse = (x: number | null): string =>
                x === null ? '0' : `1/(${x < 0 ? '−' : '+'}${Math.abs(x).toFixed(1)})`;
            facts.push({
                label: 'Lensmaker',
                value: `1/f = (${p.data.n.toFixed(2)}/${w.medium.toFixed(2)} − 1)(${inverse(p.data.r1)} − ${inverse(p.data.r2)})`,
            });
        }
        return facts;
    },

    name: (p, w) => numbered(p as Part, w, 'L'),
};
